import React, { useEffect, useState, useContext, ReactElement } from "react";
import { useFormik } from "formik";
import { useSelector, shallowEqual } from "react-redux";
import { useTranslation } from "react-i18next";
import Select from "react-select";
import * as Yup from "yup";

import {
  signInAnonymous,
  uploadFileToStorage,
  onSnapshotRegistrationSettings,
} from "../../../apis/firebase";

let snapshotRef = null;

const AnonymousForm = (): ReactElement => {
  const [loginError, setLoginError] = useState("");
  const [anonymousMandatoryField, setAnonymousMandatoryField] = useState();
  const [customFields, setCustomField] = useState([]);

  const { generalSettings } = useSelector(
    ({ generalSettings }) => ({ generalSettings }),
    shallowEqual
  );

  useEffect(() => {
    snapshotRef = onSnapshotRegistrationSettings((dt) => {
      const { customFields: _customFields } = { ...dt };
      setCustomField(_customFields || []);
    });

    return () => snapshotRef();
  }, []);

  useEffect(() => {
    if (!generalSettings) return;

    setAnonymousMandatoryField(generalSettings.anonymousMandatoryFields);
  }, [generalSettings]);

  let initialValues = {};

  const { t } = useTranslation();

  let validationObj = { displayName: "", firstName: "", lastName: "" };

  if (anonymousMandatoryField === "displayName") {
    validationObj = {
      displayName: Yup.string().required("Required"),
    };
  } else {
    validationObj = {
      firstName: Yup.string().required("Required"),
      lastName: Yup.string().required("Required"),
    };
  }

  const formValidatorObject = validationObj;

  if (!!customFields?.length) {
    customFields.forEach((d) => {
      initialValues[d.fieldName] = "";
      let _req: any;
      switch (d.type) {
        case "text":
          if (d.validationCriteria.required) {
            _req = Yup.string().required(t("FORM_REQUIRED_FIELD"));
          }
          break;
        case "checkbox":
          if (d.validationCriteria.required) {
            _req = Yup.string().required(t("FORM_REQUIRED_FIELD"));
          }
          break;
        case "file":
          if (d.validationCriteria.required) {
            _req = Yup.string().required(t("FORM_REQUIRED_FIELD"));
          }
          break;
        case "select":
          if (d.selectSize > 1) {
            _req = Yup.array().of(
              Yup.string().required(t("FORM_REQUIRED_FIELD"))
            );
          } else if (d.validationCriteria.required) {
            _req = Yup.string().required(t("FORM_REQUIRED_FIELD"));
          }
          break;
      }
      formValidatorObject[d.fieldName] = _req;
    });
  }
  //   console.log(formValidatorObject);
  const FormSchema = Yup.object().shape(formValidatorObject);
  const formik = useFormik({
    initialValues,
    validationSchema: FormSchema,
    enableReinitialize: true,
    onSubmit: async (values) => {
      try {
        if (customFields) {
          const _customFieldFileType = customFields?.find(
            (d) => d.type === "file"
          );

          if (_customFieldFileType && _customFieldFileType?.fieldName) {
            values[_customFieldFileType?.fieldName + "_file"] =
              await uploadFileToStorage(
                values[_customFieldFileType?.fieldName],
                values[_customFieldFileType?.fieldName].name
              );
            delete values[_customFieldFileType?.fieldName];
          }
        }
        if (!values?.track) {
          values["track"] = "default";
        }

        if (anonymousMandatoryField !== "displayName") {
          values["displayName"] = values.firstName + " " + values.lastName;
        }

        await signInAnonymous(values);
      } catch (e) {
        console.log(e);
        if (e === "auth/user-not-found" || e === "auth/wrong-password") {
          setLoginError(t("ERROR_LOGIN_WRONG_CREDENTIAL"));
        } else {
          setLoginError(t("ERROR_LOGIN_SOMETHING_WRONG"));
        }
      }
    },
  });

  return (
    <form onSubmit={formik.handleSubmit}>
      {/* {JSON.stringify(formik.errors)} */}
      {loginError && (
        <div className="alert alert-danger py-1 px-2 fs-7" role="alert">
          <i className="bi bi-exclamation-circle-fill me-1"></i>
          {loginError}
        </div>
      )}

      <>
        {anonymousMandatoryField === "displayName" && (
          <div className="mb-3">
            <input
              type="text"
              name="displayName"
              className={`form-control ${
                formik.errors.displayName ? "is-invalid" : ""
              }`}
              placeholder={t("FORM_DISPLAY_NAME_LABEL")}
              {...formik.getFieldProps("displayName")}
            />
            {formik.touched.displayName && formik.errors.displayName ? (
              <div className="invalid-feedback error-block">
                {formik.errors.displayName}
              </div>
            ) : null}
          </div>
        )}

        {anonymousMandatoryField === "firstNameLastName" && (
          <>
            <div className="mb-3">
              <input
                type="text"
                name="firstName"
                className={`form-control ${
                  formik.errors.firstName ? "is-invalid" : ""
                }`}
                placeholder={t("FORM_FIRST_NAME")}
                {...formik.getFieldProps("firstName")}
              />
              {formik.touched.firstName && formik.errors.firstName ? (
                <div className="invalid-feedback error-block">
                  {formik.errors.firstName}
                </div>
              ) : null}
            </div>
            <div className="mb-3">
              <input
                type="text"
                name="lastName"
                className={`form-control ${
                  formik.errors.lastName ? "is-invalid" : ""
                }`}
                placeholder={t("FORM_LAST_NAME")}
                {...formik.getFieldProps("lastName")}
              />
              {formik.touched.lastName && formik.errors.lastName ? (
                <div className="invalid-feedback error-block">
                  {formik.errors.lastName}
                </div>
              ) : null}
            </div>
          </>
        )}

        {!!customFields?.length &&
          customFields.map((d, i) => {
            const _type = d.validationCriteria.emailField ? "email" : d.type;

            return (
              <div key={i} className="mb-3">
                <div className="input-group ">
                  {(d.type === "text" ||
                    d.type === "number" ||
                    d.type === "email") && (
                    <>
                      <input
                        type={_type}
                        placeholder={d.placeholder}
                        className={`form-control ${
                          formik.errors[`${d.fieldName}`] ? "is-invalid" : ""
                        }`}
                        {...formik.getFieldProps(`${d.fieldName}`)}
                      />
                    </>
                  )}

                  {d.type === "file" && (
                    <>
                      <input
                        type={d.type}
                        id="files"
                        className={`form-control  ${
                          formik.errors[`${d.fieldName}`] ? "is-invalid" : ""
                        }`}
                        onChange={(event) => {
                          const files = event.target.files[0];
                          formik.setFieldValue(`${d.fieldName}`, files);
                        }}
                      />
                    </>
                  )}

                  {d.type === "checkbox" && (
                    <div className="d-flex flex-wrap align-items-center ">
                      <input
                        type={d.type}
                        placeholder={d.placeholder}
                        className={`form-check-input  ${
                          formik.errors[`${d.fieldName}`] ? "is-invalid" : ""
                        }`}
                        {...formik.getFieldProps(`${d.fieldName}`)}
                      />
                      <label className="fs-7 form-check-label ms-2">
                        {d.label}
                      </label>
                    </div>
                  )}

                  {d.type === "select" &&
                    (!d.selectSize || d.selectSize === 1) && (
                      <Select
                        className={`custom-select col-12 ${
                          formik.errors[`${d.fieldName}`] ? "is-invalid" : ""
                        }`}
                        options={d.options}
                        placeholder={d.label}
                        onChange={(v) => {
                          formik.setFieldValue(`${d.fieldName}`, v.value);
                        }}
                      />
                    )}

                  {d.type === "select" && (!d.selectSize || d.selectSize > 1) && (
                    <Select
                      className={`custom-select col-12 ${
                        formik.errors[`${d.fieldName}`] ? "is-invalid" : ""
                      }`}
                      options={d.options}
                      isMulti
                      placeholder={d.label}
                      onChange={(value) => {
                        formik.setFieldValue(
                          `${d.fieldName}`,
                          value?.map((d) => d?.value)
                        );
                      }}
                    />
                  )}
                  {formik.touched[`${d.fieldName}`] &&
                  formik.errors[`${d.fieldName}`] ? (
                    <div className="invalid-feedback error-block">
                      {formik.errors[`${d.fieldName}`]}
                    </div>
                  ) : null}
                </div>
              </div>
            );
          })}
      </>

      <div className="mt-2">
        <button type="submit" className="btn btn-primary fw-bold w-100">
          {t("FORM_BUTTON_LOGIN_LABEL")}
        </button>
      </div>
    </form>
  );
};

export default AnonymousForm;
